/**
 * Resolved-vs-typed state for the Client picker in the filter drawer.
 *
 * SOUND-680. The picker keeps its search text in local component state and only
 * writes `companyId` to the draft when the user CLICKS a result. Typing a client
 * name and hitting Apply Filters therefore applied nothing, silently — the
 * drawer closed, the list didn't change, and there was no way to tell that from
 * "this client genuinely has no MIDs".
 *
 * That is what the partner who filed the bug described ("input a client name in
 * the client box and click on apply filter...it is not working"), and it traps
 * internal users identically.
 *
 * The decision is lifted out of the component because sound-app has no
 * component-test harness — four defects in the SOUND-584 sprint were catchable
 * only by a human clicking. A pure function is the part that can be pinned down.
 */

export const PICKER_SELECTED = "selected";
export const PICKER_UNRESOLVED = "unresolved";
export const PICKER_EMPTY = "empty";

/**
 * @param {object}  args
 * @param {string} [args.value]  the selected companyId ("" / null when none)
 * @param {string} [args.query]  the raw text currently in the search box
 * @returns {"selected"|"unresolved"|"empty"}
 *
 *   selected    a company is chosen; the filter will apply
 *   unresolved  the user typed something but never picked — the trap
 *   empty       nothing typed, nothing chosen; the filter is simply unused
 */
export function companyPickerState({ value, query } = {}) {
  if (typeof value === "string" && value.trim()) return PICKER_SELECTED;
  if (typeof query === "string" && query.trim()) return PICKER_UNRESOLVED;
  return PICKER_EMPTY;
}

/**
 * The message to show under the input, or null when there is nothing to say.
 *
 * Only the unresolved state gets one. Saying "no filter applied" in the empty
 * state would put a warning on every unused filter in the drawer.
 */
export function companyPickerHint(state) {
  return state === PICKER_UNRESOLVED
    ? "Not applied yet — pick a client from the list."
    : null;
}

/**
 * Does this picker have typed text that will be DISCARDED on apply?
 *
 * Kept separate from the hint so a caller can act on the condition (e.g. warn in
 * the drawer footer) without depending on the wording.
 */
export function hasUnappliedCompanyText(args) {
  return companyPickerState(args) === PICKER_UNRESOLVED;
}
